const ONE_MB = 1_048_576;
const DEFAULT_QUALITIES = [0.86, 0.8, 0.74, 0.68, 0.62, 0.55];
const MIN_DIMENSION = 640;
const RESIZE_FACTOR = 0.85;
const ALPHA_SAMPLE_DIMENSION = 256;
// Re-encoding these through a canvas is destructive: animation is flattened to one frame and
// vectors are rasterised. Leave them exactly as uploaded.
const UNTOUCHABLE_TYPES = new Set(["image/gif", "image/svg+xml"]);

/**
 * Card-sized artwork (catalogue category cards, ~300 CSS px wide). 1200 px covers 3x DPR, and
 * WebP keeps the transparency the category PNGs rely on.
 */
export const CARD_IMAGE_RESIZE = Object.freeze({
  maxWidth: 1200,
  maxHeight: 1200,
  maxBytes: 200_000,
  preferWebp: true,
});

/** Full-bleed artwork (hero/section backgrounds and video posters). */
export const FULL_WIDTH_IMAGE_RESIZE = Object.freeze({
  maxWidth: 1920,
  maxHeight: 1920,
  maxBytes: 500_000,
  preferWebp: true,
});

let supportsWebpEncodingPromise;

function readImageSource(file) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file).then((bitmap) => ({
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      cleanup: () => bitmap.close(),
    }));
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      resolve({
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        cleanup: () => URL.revokeObjectURL(objectUrl),
      });
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to decode image"));
    };

    image.src = objectUrl;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
}

function getExtensionFromType(type) {
  if (type === "image/webp") return "webp";
  if (type === "image/png") return "png";
  return "jpg";
}

/**
 * Whether any pixel is less than fully opaque. Sampled from a downscaled copy — a transparent
 * region survives the downscale as a sub-255 alpha, and it keeps the scan cheap. Anything that
 * goes wrong answers "yes", because the expensive mistake is flattening transparency to black.
 */
function hasAlphaChannel(source, width, height) {
  try {
    const scale = Math.min(ALPHA_SAMPLE_DIMENSION / width, ALPHA_SAMPLE_DIMENSION / height, 1);
    const sampleWidth = Math.max(1, Math.round(width * scale));
    const sampleHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = sampleWidth;
    canvas.height = sampleHeight;
    const context = canvas.getContext("2d");
    if (!context) return true;

    context.clearRect(0, 0, sampleWidth, sampleHeight);
    context.drawImage(source, 0, 0, sampleWidth, sampleHeight);
    const { data } = context.getImageData(0, 0, sampleWidth, sampleHeight);
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) return true;
    }
    return false;
  } catch (_error) {
    return true;
  }
}

/**
 * Picks the encoder. WebP first when asked for: it is the smallest and it keeps alpha. JPEG is
 * only safe when there is no transparency to lose, so a PNG source falls back to PNG unless the
 * pixels turn out to be opaque.
 */
async function resolveOutputType({ file, preferWebp, source, width, height }) {
  if (preferWebp && (await supportsWebpEncoding())) {
    return "image/webp";
  }
  if (file.type === "image/png" && hasAlphaChannel(source, width, height)) {
    return "image/png";
  }
  return "image/jpeg";
}

function supportsWebpEncoding() {
  if (!supportsWebpEncodingPromise) {
    supportsWebpEncodingPromise = (async () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 1;
        canvas.height = 1;
        const blob = await canvasToBlob(canvas, "image/webp", 0.8);
        return Boolean(blob && blob.type === "image/webp");
      } catch (_error) {
        return false;
      }
    })();
  }

  return supportsWebpEncodingPromise;
}

export async function resizeImageClient(
  file,
  {
    maxWidth = 1920,
    maxHeight = 1920,
    maxBytes = ONE_MB,
    // Keep default JPG for compatibility with PDF generation (react-pdf image parser).
    preferWebp = false,
    qualities = DEFAULT_QUALITIES,
  } = {}
) {
  if (!(file instanceof File && file.type.startsWith("image/"))) {
    return file;
  }

  if (UNTOUCHABLE_TYPES.has(file.type)) {
    return file;
  }

  // Force optimization only when file exceeds threshold.
  if (file.size <= maxBytes) {
    return file;
  }

  let decoded;
  try {
    decoded = await readImageSource(file);
  } catch (_error) {
    return file;
  }

  const { source, width, height, cleanup } = decoded;

  try {
    const outputType = await resolveOutputType({ file, preferWebp, source, width, height });
    // PNG is lossless: the quality argument does nothing, so one encode per size is enough.
    const encodeQualities = outputType === "image/png" ? [undefined] : qualities;
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return file;

    const initialScale = Math.min(maxWidth / width, maxHeight / height, 1);
    let targetWidth = Math.max(1, Math.round(width * initialScale));
    let targetHeight = Math.max(1, Math.round(height * initialScale));

    let bestBlob = null;

    for (let step = 0; step < 8; step += 1) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      context.clearRect(0, 0, targetWidth, targetHeight);
      context.drawImage(source, 0, 0, targetWidth, targetHeight);

      for (const quality of encodeQualities) {
        const blob = await canvasToBlob(canvas, outputType, quality);
        if (!blob) continue;

        if (!bestBlob || blob.size < bestBlob.size) {
          bestBlob = blob;
        }

        if (blob.size <= maxBytes) {
          bestBlob = blob;
          break;
        }
      }

      if (bestBlob && bestBlob.size <= maxBytes) {
        break;
      }

      if (targetWidth <= MIN_DIMENSION && targetHeight <= MIN_DIMENSION) {
        break;
      }

      targetWidth = Math.max(MIN_DIMENSION, Math.round(targetWidth * RESIZE_FACTOR));
      targetHeight = Math.max(MIN_DIMENSION, Math.round(targetHeight * RESIZE_FACTOR));
    }

    // Re-encoding can come out bigger than the original (a lossless PNG round-trip especially).
    // Uploading that would make things worse, so keep the file the admin picked.
    if (!bestBlob || bestBlob.size >= file.size) {
      return file;
    }

    const extension = getExtensionFromType(outputType);
    const baseName = file.name.replace(/\.[^.]+$/, "");

    return new File([bestBlob], `${baseName}.${extension}`, {
      type: outputType,
      lastModified: Date.now(),
    });
  } finally {
    cleanup();
  }
}
